<!-- Takes no focus (focusStateEnabled false): on the returns page the scan
     box keeps it, so a parcel scanned while this is open still lands in the
     box. -->
<dx-popup
    [(visible)]="visible"
    [showTitle]="true"
    [title]="title"
    [dragEnabled]="true"
    [hideOnOutsideClick]="true"
    [showCloseButton]="true"
    [focusStateEnabled]="false"
    [width]="680"
    height="auto"
    [maxHeight]="'90vh'"
    (onHidden)="onHidden()"
>
    @if (pkg; as p) {
        <div class="return-detail">
            <div class="kind">
                <span class="source source-{{ p.source }}">{{
                    sourceLabels[p.source]
                }}</span>
                <span class="kind-text">{{
                    sourceDescriptions[p.source]
                }}</span>
            </div>

            <dl class="fields">
                <dt>ร้านค้า</dt>
                <dd>{{ p.storeTitle }}</dd>

                <dt>คำสั่งซื้อ</dt>
                <dd class="code">{{ p.orderNo }}</dd>

                @if (p.returnId) {
                    <dt>เลขคำขอคืน</dt>
                    <dd class="code">{{ p.returnId }}</dd>
                }

                <dt>ประเภทคำขอ</dt>
                <dd>
                    {{ returnTypeLabel }}
                    @if (returnTypeLabel !== p.returnType) {
                        <span class="muted">({{ p.returnType }})</span>
                    }
                </dd>

                @if (p.reason) {
                    <dt>เหตุผล</dt>
                    <dd>{{ p.reason }}</dd>
                }

                <dt>
                    {{
                        p.source === "failed_delivery"
                            ? "เลขพัสดุ (ขาไป)"
                            : "เลขพัสดุคืน"
                    }}
                </dt>
                <dd class="code">
                    @if (p.trackingNumber) {
                        {{ p.trackingNumber }}
                    } @else {
                        <span class="muted">ยังไม่มี</span>
                    }
                </dd>

                @if (p.carrier) {
                    <dt>ขนส่ง</dt>
                    <dd>
                        {{ p.carrier }}
                        @if (p.carrierInferred) {
                            <span class="muted"
                                >(แพลตฟอร์มไม่ระบุ อนุมานจากเลขพัสดุ)</span
                            >
                        }
                    </dd>
                }

                <dt>สถานะพัสดุ</dt>
                <dd>
                    <span class="stage stage-{{ p.packageStage }}">{{
                        stageLabels[p.packageStage]
                    }}</span>
                    @if (p.logisticsStatus) {
                        <span class="muted">{{ p.logisticsStatus }}</span>
                    }
                </dd>

                <dt>สถานะแพลตฟอร์ม</dt>
                <dd>{{ p.platformStatus || "–" }}</dd>

                @if (restock; as r) {
                    <dt>สต็อก lnwshop</dt>
                    <dd>
                        <span class="restock restock-{{ r.verdict }}">{{
                            r.note
                        }}</span>
                        @if (checkingRestock) {
                            <span class="muted"
                                >(กำลังตรวจกับข้อมูล lnwshop…)</span
                            >
                        }
                    </dd>
                }

                <dt>รับที่ร้าน</dt>
                <dd>
                    @if (receipt; as r) {
                        <span class="received"
                            ><i class="dx-icon-check"></i> ยืนยันรับแล้ว
                            {{ r.at | thaiDateTime }}</span
                        >
                        @if (r.by) {
                            <span class="muted">· {{ r.by }}</span>
                        }
                    } @else {
                        <span class="muted">ยังไม่ยืนยันรับ</span>
                    }
                </dd>

                @if (p.createdAt) {
                    <dt>{{ createdLabel }}</dt>
                    <dd>
                        {{ p.createdAt | thaiDateTime }}
                        <span class="muted"
                            >({{
                                p.createdAt | formatRelativeDatetime: "th"
                            }})</span
                        >
                    </dd>
                }

                @if (p.updatedAt) {
                    <dt>อัปเดตล่าสุด</dt>
                    <dd>
                        {{ p.updatedAt | thaiDateTime }}
                        <span class="muted"
                            >({{
                                p.updatedAt | formatRelativeDatetime: "th"
                            }})</span
                        >
                    </dd>
                }

                @if (p.refundAmount !== null) {
                    <dt>เงินคืน</dt>
                    <dd>฿{{ p.refundAmount | formatNumber: 2 }}</dd>
                }
            </dl>

            <div class="section-title">
                สินค้า {{ p.items.length }} รายการ · {{ totalUnits }} ชิ้น
            </div>
            <table class="items">
                <thead>
                    <tr>
                        <th>SKU</th>
                        <th>สินค้า</th>
                        <th class="num">จำนวน</th>
                    </tr>
                </thead>
                <tbody>
                    @for (item of p.items; track item.sku) {
                        <tr>
                            <td class="sku">
                                @if (item.sku) {
                                    <a
                                        class="sku-link"
                                        (click)="skuClick.emit(item.sku)"
                                        >{{ item.sku }}</a
                                    >
                                } @else {
                                    <span class="muted">ไม่มี SKU</span>
                                }
                            </td>
                            <td>{{ item.name }}</td>
                            <td class="num">{{ item.qty }}</td>
                        </tr>
                    } @empty {
                        <tr>
                            <td colspan="3" class="muted">ไม่มีรายการสินค้า</td>
                        </tr>
                    }
                </tbody>
            </table>

            @if (p.trackingEvents.length > 0) {
                <div class="section-title">ประวัติขนส่ง</div>
                <ol class="timeline">
                    @for (event of p.trackingEvents; track $index) {
                        <li [class.latest]="$first">
                            <span class="when">{{
                                event.at | thaiDateTime
                            }}</span>
                            <span class="what">
                                {{ event.description || event.status }}
                                @if (event.description && event.status) {
                                    <span class="muted">{{
                                        event.status
                                    }}</span>
                                }
                            </span>
                        </li>
                    }
                </ol>
            }

            <div class="actions">
                <dx-button
                    stylingMode="contained"
                    text="ปิด"
                    (onClick)="close()"
                ></dx-button>
            </div>
        </div>
    }
</dx-popup>
